import axios from 'axios';
import TokenService from './tokenService';
import { ENDPOINTS } from './endpoints';
import { redirectToLogin, redirectToLoginIfUnauthorized } from './authRedirect';
import {
  USERS_API,
  PAYMENTS_API,
  USER_BALANCE_API,
  ADD_BALANCE_API,
  PRICING_API,
  EMBEDDED_USERS_API,
  INSERT_USER_API,
  MESSAGE_STATS_ALL_API,
} from './adminApiEndpoints';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

const attachAuthErrorInterceptor = (client) => {
  client.interceptors.response.use(
    (res) => res,
    (error) => {
      redirectToLoginIfUnauthorized(error);
      return Promise.reject(error);
    }
  );
};

attachAuthErrorInterceptor(axios);
attachAuthErrorInterceptor(api);

export const loginUser = (payload) => {
  return axios
    .post(ENDPOINTS.LOGIN, payload, { credentials: 'include' })
    .then((response) => {
      const token = response.data?.token;
      if (!token) throw new Error('No token received from the server.');
      TokenService.setToken(token);
      return response.data;
    })
    .catch((error) => {
      if (error.response) {
        throw new Error(error.response.data?.message || 'Login failed');
      } else if (error.request) {
        throw new Error('No response from the server');
      }
      throw new Error(error.message);
    });
};

export const getToken = () => {
  const token = TokenService.getToken();
  if (!token) {
    redirectToLogin();
    return null;
  }
  return token;
};

export const getUserBalance = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(
    USER_BALANCE_API,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data; // expect { balance: number }
};

export const getMyInfo = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.get(ENDPOINTS.MY_INFO, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data; // expect { userInfo: { name: string, ... } }
};

// Campaign Templates (Summary list) — POST /templates/get-templates
export const fetchCampaignTemplates = async ({
  page = 1,
  limit = 10,
  from_date,
  to_date,
} = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const body = { page, limit };
  if (from_date) body.from_date = from_date;
  if (to_date) body.to_date = to_date;
  const res = await axios.post(ENDPOINTS.GET_CAMP_TEMPLATES(), body, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Expecting { templates: [...] }
  return res.data?.templates || [];
};

export const fetchTemplateCounts = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.get(ENDPOINTS.GET_WA_TEMPLATE_COUNTS, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return Array.isArray(res.data?.templateCounts) ? res.data.templateCounts : [];
};

export const createTemplate = async (templateName, templateDescription) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(
    ENDPOINTS.CREATE_TEMPLATE,
    {
      name: templateName,
      description: templateDescription,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  return res.data?.template || res.data;
};

export const fetchWaTemplates = async ({ page = 1, limit = 10 } = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.get(ENDPOINTS.FETCH_TEMPLATES(page, limit), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data || { templates: [] };
};

export const createWaTemplate = async (payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(ENDPOINTS.CREATE_WA_TEMPLATE, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  return res.data;
};

export const uploadTemplateMedia = async (file) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const formData = new FormData();
  formData.append('mediaFile', file);

  const res = await api.post(ENDPOINTS.UPLOAD_TEMPLATE_MEDIA, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

export const fetchWaApTemplates = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.GET_WA_AP_TEMPLATES, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const mapTemplateToCampaign = async ({ templateId, waTemplateId, userId }) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(
    ENDPOINTS.TEMPLATE_MAPPING,
    {
      template_id: templateId,
      wa_template_id: waTemplateId,
      userid: userId,
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const uploadCampaignMedia = async (file) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const formData = new FormData();
  formData.append('mediaFile', file);

  const res = await api.post(ENDPOINTS.UPLOAD_CAMPAIGN_MEDIA, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

export const sendCampaignMessage = async (payload = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(ENDPOINTS.SEND_CAMPAIGN_MESSAGE, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const sendCustomizedCampaignMessage = async (formData) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(ENDPOINTS.SEND_CUSTOMISED_CAMPAIGN_MESSAGE, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

export const getCampaignReport = async (templateId) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(
    ENDPOINTS.FETCH_REPORT(templateId),
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const getStatusData = async (templateId, status, page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(
    ENDPOINTS.FETCH_STATUS(templateId, status, page, limit),
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Detailed message report (paginated)
export const getCampaignMessageReport = async (body = {}, page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.GET_CAMPAIGN_REPORT,
    { ...body, page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data || { data: [], pagination: { total_pages: 1, current_page: 1 } };
};

// Full detailed message report for CSV download
export const getCampaignMessageFullReport = async (body = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.GET_CAMPAIGN_REPORT,
    { ...body, page: 1, limit: 50000 },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data?.data || [];
};

// Number message report
export const getNumberMessageReport = async (body = {}, page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.NUMBER_MESSAGE_REPORT,
    { ...body, page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data || { data: [], pagination: { total_pages: 1, current_page: 1 } };
};

// User payments (logged-in user from token)
export const getUserPayments = async (page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    PAYMENTS_API,
    { page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

/** Payments for a specific user (admin) — PAYMENTS_API + `user_id` in body (legacy admin). */
export const getUserPaymentsForUser = async (userId, page = 1, limit = 100) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    PAYMENTS_API,
    { user_id: Number(userId), page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Admin APIs
export const getUsers = async (page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    USERS_API,
    { page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

/** Admin: all users’ message stats for a period (`POST /user/message-stats-all`). */
export const getMessageStatsAll = async ({ period, page = 1, limit = 1000 }) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    MESSAGE_STATS_ALL_API,
    { period, page, limit },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  return res.data;
};

export const updateUserStatus = async (userId, status) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.put(
    `${USERS_API}/${userId}`,
    { status },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const getPricingOptions = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    PRICING_API,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const getEmbeddedUsers = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    EMBEDDED_USERS_API,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const createManagedUser = async (payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    INSERT_USER_API,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const getUserBalanceById = async (userId) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    USER_BALANCE_API,
    { user_id: Number(userId) },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const addUserBalance = async (userId, amount) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ADD_BALANCE_API,
    { user_id: Number(userId), amount: Number(amount) },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Chat APIs
export const getChatHistory = async (waId, page = 1, limit = 20) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.GET_CHAT_HISTORY,
    { wa_id: waId, page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const sendMessageAPI = async (wa_id, message_body) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.SEND_WHATSAPP,
    { wa_id, message_body },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Bot flow responses
export const getUserFlows = async (page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.GET_USER_FLOWS, {
    params: { page, limit },
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getFlowResponsesByType = async (flowType, page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.GET_FLOW_RESPONSES, {
    params: { flowType, page, limit },
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// Incoming messages list
export const getIncomingMessages = async (page = 1, limit = 10, fromDate, toDate) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.GET_INCOMING_MESSAGES(page, limit, fromDate, toDate),
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data; // expect { messages: [...], pagination? }
};
